import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, FormProvider, useForm } from 'react-hook-form';
import { Grid, TextField, Typography, Button, Box } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt';
import RepeatIcon from '@mui/icons-material/Repeat';
import PersonIcon from '@mui/icons-material/Person';
import TimeToLeaveIcon from '@mui/icons-material/TimeToLeave';

import { useStyles } from 'features/salesOrder/components/SalesOrderServiceForm/components/InsuranceServiceForm/style';
import {
	typeTransfer,
	typeUnit,
	ICodeName,
	IServiceCostVariationLogs,
	ITransferService,
	TypeServiceEnum,
	TransferTypeEnum,
} from 'features/salesOrder/types';
import { checkShowErrorMessage } from 'features/common/helpers';
import { ControlledAutoComplete } from 'features/common/components/ControlledAutoComplete';
import { ControlledTextField } from 'features/common/components/ControlledTextField';
import { ControlledCheckBox } from 'features/common/components/ControlledCheckBox';
import { ControlledSelect } from 'features/common/components/ControlledSelect';
import { ControlledDatePicker } from 'features/common/components/ControlledDatePicker';
import { StayDurationHint } from 'features/common/components/StayDurationHint';
import { AutocompleteProviders } from 'features/common/components/AutocompleteProviders';
import {
	calcularPrecioFinal,
	calculatMarkupPercentaje,
	excludeTypeUnits,
	filterServiceCostVariationLogs,
} from 'features/salesOrder/components/SalesOrderServiceForm/helpers';
import { useSaleOrderId } from '../../../context/SaleOrderScopeContext';
import { useServiceQuickEdit } from '../../../context/ServiceQuickEditContext';
import { executeServicePut } from '../../../helpers/executeServicePut';
import { getCostVariationLogsByService } from 'features/salesOrder/services';
import { ShowAlertState } from 'features/common/types';
import { ServiceCostVariationLogs } from 'features/salesOrder/components/SalesOrderServiceForm/components/ServiceCostVariationLogs';
import { getRates } from 'features/stats/services';
import { useServiceMarketCountry } from '../../../hooks/useServiceMarketCountry';
import { useVoucherDownload } from 'features/common/hooks/useVoucherDownload';
import {
	shouldShowVoucherButton,
	generateServicePassengers,
	getServiceTypeForVoucherAPI,
} from 'features/common/helpers/voucherHelpers';
import { VoucherDownloadModal } from 'features/common/components/VoucherDownloadModal';
import GetAppIcon from '@mui/icons-material/GetApp';
import ServiceBillingStatusDetail from '../ServiceBillingStatusDetail';
import ServicePurchaseOrderDetail from '../ServicePurchaseOrderDetail';
import { ProviderPaymentDateSection } from '../ProviderPaymentDateSection';
import { useServiceProviderPaymentConstraints } from '../../../hooks/useServiceProviderPaymentConstraints';
import ProviderPaymentConstraintsAlert from '../ProviderPaymentConstraintsAlert';
import ReferenceValidationAlert from '../ReferenceValidationAlert';
import { SERVICE_FORM_INITIAL_FOCUS_ID } from '../../../helpers';
import {
	buildMinTotalCostRules,
	getTariffProviderIdFromFormData,
	getTariffProviderIdFromService,
	validateProviderPaymentConstraintsBeforeSave,
} from '../../../helpers/providerPaymentConstraints';

interface Props {
	index: number;
	passengers: { keyId: string; name: string; lastName: string }[];
	service: ITransferService;
	currencyesList: ICodeName[];
	onUpdateService: () => void;
	onSetAlert: (values: ShowAlertState) => void;
	onSetLoading: (value: boolean) => void;
	isExistingService?: boolean;
}
const iconMapping = {
	Persona: <PersonIcon />,
	Vehiculo: <TimeToLeaveIcon />,
};
const iconType = {
	Ida: <ArrowRightAltIcon />,
	'Ida y vuelta': <RepeatIcon />,
};

export const TransferService = ({
	index,
	passengers,
	service,
	currencyesList,
	onUpdateService,
	onSetAlert,
	onSetLoading,
	isExistingService,
}: Props): JSX.Element => {
	const classes = useStyles();
	const methods = useForm();
	const isServiceInactive = service.enable === false;
	const [tariffProviderId, setTariffProviderId] = useState('n/a');
	const saleOrderId = useSaleOrderId();
	const quickEdit = useServiceQuickEdit();

	// Hook para manejo de descarga de vouchers
	const {
		isModalOpen: isVoucherModalOpen,
		voucherData,
		isLoading: isVoucherLoading,
		error: voucherError,
		handleDownloadVouchers,
		handleForceUpdate,
		closeModal: closeVoucherModal,
	} = useVoucherDownload();
	const {
		control,
		formState: { errors },
		getValues,
		setValue,
		resetField,
		watch,
	} = methods;
	const { fields, append, remove } = useFieldArray({
		control,
		// @ts-ignore
		name: 'transferSegments',
	});
	const idService = service.keyId;
	const [serviceCostVariationLogs, setServiceCostVariationLogs] = useState<IServiceCostVariationLogs[]>([]);
	const unit = watch('unit' as `${string}.${string | number}`);
	const saleAmount = watch('saleAmount' as `${string}.${string | number}`);
	const referenceValue = watch('reference' as `${string}.${string | number}`) as string;
	const tariffProviderIdWatched =
		(watch('tariffProviderId' as `${string}.${string | number}`) as string) || tariffProviderId;

	const theCountry = useServiceMarketCountry();
	const paymentConstraints = useServiceProviderPaymentConstraints(service.keyId);
	const initialTariffProviderId = useMemo(() => getTariffProviderIdFromService(service), [service]);

	const filteredArray = filterServiceCostVariationLogs(serviceCostVariationLogs, idService);

	const loadCostLog = useCallback(async () => {
		const result = await getCostVariationLogsByService(saleOrderId, idService, quickEdit?.agencyId);
		setServiceCostVariationLogs(result.data);
	}, [saleOrderId, idService, quickEdit?.agencyId]);

	const handleCostChange = (newCost: number) => {
		// @ts-ignore
		setValue('cost', newCost, { shouldDirty: true });
		const saleAmount = getValues('saleAmount' as `${string}.${string | number}`) || 0;
		const markupPercentage = calculatMarkupPercentaje(newCost, saleAmount) as string;
		// @ts-ignore
		setValue('markupPercentage', markupPercentage, { shouldDirty: true });
		calculateTotalCost();
	};

	const handleSaleAmountChange = (newValue: number) => {
		if (theCountry === 'CO') {
			const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
			const valorLocalCurrency = newValue * rateValue;
			setValue('saleAmountInLocalCurrency', valorLocalCurrency, { shouldDirty: true });
		}

		// @ts-ignore
		setValue('saleAmount', newValue, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;

		if (newValue === 0) {
			setValue('markupPercentage', 0);
		} else {
			const newMarkup = calculatMarkupPercentaje(cost, newValue) as string;

			// @ts-ignore
			setValue('markupPercentage', newMarkup, { shouldDirty: true });
		}
		calculateTotalCost();
	};

	const handleSaleAmountChangeCOP = (valorEnCop: number) => {
		setValue('saleAmountInLocalCurrency', valorEnCop, { shouldDirty: true });
		const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
		const valorEnDolares = valorEnCop / rateValue;
		// @ts-ignore
		setValue('saleAmount', valorEnDolares, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;

		if (valorEnDolares === 0) {
			setValue('markupPercentage', 0);
		} else {
			const newMarkup = calculatMarkupPercentaje(cost, valorEnDolares) as string;

			// @ts-ignore
			setValue('markupPercentage', newMarkup, { shouldDirty: true });
		}
		calculateTotalCost();
	};

	const handleMarkupPercentageChange = (newValue: number) => {
		// @ts-ignore
		setValue('markupPercentage', newValue, { shouldDirty: true });
		const cost = getValues('cost' as `${string}.${string | number}`) || 0;
		const newSaleAmount = calcularPrecioFinal(cost, newValue) as string;

		if (theCountry === 'CO') {
			const rateValue = getValues('currencyRate' as `${string}.${string | number}`) || 1;
			const valorLocalCurrency = Number(newSaleAmount) * rateValue;
			setValue('saleAmountInLocalCurrency', valorLocalCurrency, { shouldDirty: true });
		}

		// @ts-ignore
		setValue('saleAmount', newSaleAmount, { shouldDirty: true });
		calculateTotalCost();
	};

	const sameTariffServiceProvider = watch('sameTariffServiceProvider' as `${string}.${string | number}`, false);

	const handleCheckboxChange = () => {
		const updatedValue = !sameTariffServiceProvider;
		setValue('sameTariffServiceProvider' as `${string}.${string | number}`, updatedValue);
	};

	const passengersSelected = watch('passengerIds' as `${string}.${string | number}`) || [];

	const calculateTotalCost = useCallback(() => {
		let cost = getValues('cost' as `${string}.${string | number}`) || 0;
		let saleAmount = getValues('saleAmount' as `${string}.${string | number}`) || 0;

		if (unit === 'Person') {
			cost = cost * passengersSelected.length;
			saleAmount =
				saleAmount * passengersSelected.length || (saleAmount ? saleAmount * passengersSelected.length : null);
		}

		setValue('totalCost' as `${string}.${string | number}`, cost);
		setValue('totalSaleAmount' as `${string}.${string | number}`, saleAmount);

		if (theCountry === 'CO') {
			const saleAmountInLocalCurrency = getValues('saleAmountInLocalCurrency' as `${string}.${string | number}`) || 0;
			const totalEnColombianos =
				unit === 'Person' ? saleAmountInLocalCurrency * passengersSelected.length : saleAmountInLocalCurrency;
			setValue('totalSaleAmountInLocalCurrency', totalEnColombianos);
		}
	}, [getValues, passengersSelected, setValue, unit, theCountry]);

	useEffect(() => {
		if (unit === 'Person' && saleAmount !== null) {
			const newTotalSaleAmount = saleAmount * passengersSelected.length;
			setValue('totalSaleAmount' as `${string}.${string | number}`, newTotalSaleAmount);
		}
	}, [saleAmount, passengersSelected.length]);

	useEffect(() => {
		if (sameTariffServiceProvider) {
			setValue('serviceProviderId' as `${string}.${string | number}`, tariffProviderId);
		}
		if (!sameTariffServiceProvider) {
			setValue('sameTariffServiceProvider' as `${string}.${string | number}`, false);
		}
	}, [sameTariffServiceProvider, tariffProviderId]);

	const addstretches = () => {
		const newstretches = {
			transferType: '',
			originName: '',
			destinationName: '',
			startDate: '',
			endDate: '',
		};
		append(newstretches);
	};

	const handleDelete = (index: number) => {
		remove(index);
	};

	const handleUpdateService = async (data: ITransferService) => {
		try {
			onSetLoading(true);
			const transferService = data;

			if (
				!validateProviderPaymentConstraintsBeforeSave({
					constraints: paymentConstraints,
					totalCost: Number(transferService.totalCost),
					initialTariffProviderId,
					submittedTariffProviderId: getTariffProviderIdFromFormData(transferService),
					onSetAlert,
					onSetLoading,
				})
			) {
				return;
			}

			if (transferService.transferSegments) {
				transferService.transferSegments = transferService.transferSegments.map((segment) => {
					const toReturn = { ...segment };

					toReturn.startDate = segment.startDate;
					if (
						// @ts-ignore
						segment.transferType === TransferTypeEnum.InOut ||
						// @ts-ignore
						segment.transferType === TransferTypeEnum.InOutPrivate
					) {
						// Para tipos que requieren fecha de vuelta, usar la fecha seleccionada
						toReturn.endDate = segment.endDate;
					} else {
						// Para tipos que no requieren fecha de vuelta, NO enviar endDate
						// @ts-ignore - API no espera endDate para tipos de solo ida
						delete toReturn.endDate;
					}
					return toReturn;
				});
			}
			// @ts-ignore
			if (transferService.serviceProviderId === 'n/a' && !transferService.sameTariffServiceProvider) {
				// Solo limpiar si NO está marcado el checkbox
				// @ts-ignore
				delete transferService.serviceProviderId;
			}
			// Si sameTariffServiceProvider = true, asegurar coherencia
			if (transferService.sameTariffServiceProvider === true) {
				// @ts-ignore
				transferService.serviceProviderId = getValues('tariffProviderId' as `${string}.${string | number}`);
			}
			// @ts-ignore
			if (transferService.tariffProviderId === 'n/a') {
				// @ts-ignore
				delete transferService.tariffProviderId;
			}

			transferService.serviceType = TypeServiceEnum.Transfer;

			if (transferService.totalSaleAmount === null) {
				// @ts-ignore
				transferService.totalSaleAmount = 0;
			}
			if (theCountry !== 'CO') {
				// @ts-ignore
				delete transferService.currencyRate;
				// @ts-ignore
				delete transferService.totalSaleAmountInLocalCurrency;
			}

			const saved = await executeServicePut({
				saleOrderId,
				serviceId: transferService.keyId,
				data: transferService,
				quickEdit,
			});
			if (!saved) {
				onSetLoading(false);
				return;
			}
			if (!quickEdit) {
				onSetAlert({
					show: true,
					severity: 'success',
					message: 'Transfer guardado con exito!',
				});
				onUpdateService();
			}
		} catch (error) {
			// @ts-ignore
			alert(error.response.data.message);
		} finally {
			onSetLoading(false);
		}
	};

	// Función para manejar la descarga de vouchers
	const handleVoucherDownload = async () => {
		try {
			const reference = getValues('reference' as `${string}.${string | number}`);
			const passengerIds = getValues('passengerIds' as `${string}.${string | number}`) || [];

			if (!saleOrderId || !service.keyId || !tariffProviderId || !reference) {
				onSetAlert({
					show: true,
					severity: 'error',
					message: 'Faltan datos requeridos para descargar vouchers',
				});
				return;
			}

			const servicePassengers = generateServicePassengers(passengerIds, passengers);
			const serviceType = getServiceTypeForVoucherAPI(service.serviceType || 'Transfer');

			await handleDownloadVouchers({
				saleOrderId,
				powerServiceId: service.keyId,
				tariffProviderId,
				reservationCode: reference,
				serviceType,
				servicePassengers,
			});
		} catch (error) {
			onSetAlert({
				show: true,
				severity: 'error',
				message: 'Error al solicitar vouchers',
			});
		}
	};

	useEffect(() => {
		const effectTariffProviderId =
			typeof service.tariffProvider === 'string'
				? service.tariffProvider || 'n/a'
				: service.tariffProvider?.id || 'n/a';
		setTariffProviderId(effectTariffProviderId);
		const effectServiceProviderId = service.serviceProvider?.id || 'n/a';
		setValue('reference' as `${string}.${string | number}`, service.reference);
		setValue('tariffProviderId' as `${string}.${string | number}`, effectTariffProviderId);
		setValue('unit' as `${string}.${string | number}`, service.unit || '');
		setValue('cost' as `${string}.${string | number}`, service.cost || 0);
		setValue('saleAmount' as `${string}.${string | number}`, service.saleAmount || 0);
		setValue('currency' as `${string}.${string | number}`, service.currency || 0);
		setValue('keyId' as `${string}.${string | number}`, service.keyId);
		setValue('markupPercentage' as `${string}.${string | number}`, service.markupPercentage || 0);
		setValue('voucherNotes' as `${string}.${string | number}`, service.voucherNotes || '');
		setValue('internalNotes' as `${string}.${string | number}`, service.internalNotes || '');
		setValue('passengerIds' as `${string}.${string | number}`, service.passengerIds || '');
		setValue('serviceProviderId' as `${string}.${string | number}`, effectServiceProviderId);
		setValue('sameTariffServiceProvider' as `${string}.${string | number}`, service.sameTariffServiceProvider);
		setValue('transferSegments' as any, service.transferSegments || []);
		if (service.transferSegments && service.transferSegments?.length > 0) {
			service.transferSegments.forEach((segment: any) => {
				// originName: string;
				// destinationName: string;
				// startDate: string;
				// endDate: string;
				const checkIfExists = service.transferSegments?.find(
					(segmentB) =>
						segmentB.originName === segment.originName &&
						segmentB.destinationName === segment.destinationName &&
						segmentB.startDate === segment.startDate &&
						segmentB.endDate === segment.endDate,
				);
				!checkIfExists && append(segment);
			});
		}
		service.cost && handleCostChange(Number(service.cost));
		service.saleAmount && handleSaleAmountChange(Number(service.saleAmount));
		if (theCountry === 'CO') {
			if (!isExistingService) {
				const fetchRates = async () => {
					const result = await getRates();
					setValue('currencyRate', result.data.ratesList[0].values[0].effectiveRate);
				};
				fetchRates();
			} else {
				setValue('currencyRate', service.currencyRate || 1);
				setValue('saleAmountInLocalCurrency' as `${string}.${string | number}`, service.saleAmountInLocalCurrency || 1);
			}
			setValue('totalSaleAmountInLocalCurrency', service.totalSaleAmountInLocalCurrency || 1);
		}
	}, [setValue, index, append, service, theCountry, isExistingService]);

	const paymentDateHydrateKey = useMemo(() => {
		const lines = service?.providerManualPaymentDateLines ?? [];
		const linesKey = lines
			.map((l) => `${l.id ?? 'n'}|${l.paymentDate ?? ''}|${l.amount ?? ''}|${l.observation ?? ''}`)
			.join('~');

		return `${service?.keyId ?? 'no'}|${service?.automaticProviderPaymentDateCalculation === true}|${
			lines.length
		}|${linesKey}`;
	}, [service?.keyId, service?.automaticProviderPaymentDateCalculation, service?.providerManualPaymentDateLines]);

	useEffect(() => {
		if (!service?.keyId) return;

		setValue('automaticProviderPaymentDateCalculation', service.automaticProviderPaymentDateCalculation === true, {
			shouldDirty: false,
			shouldTouch: false,
			shouldValidate: false,
		});

		const normalized = (service.providerManualPaymentDateLines ?? []).map((l) => ({
			id: l.id ?? null,
			paymentDate: l.paymentDate ?? '',
			amount: l.amount ?? null,
			observation: l.observation ?? '',
		}));

		setValue('providerManualPaymentDateLines', normalized, {
			shouldDirty: false,
			shouldTouch: false,
			shouldValidate: false,
		});
	}, [paymentDateHydrateKey, setValue]);

	useEffect(() => {
		if (fields.length < 1 && service.transferSegments === undefined) {
			addstretches();
		}
	}, [fields, service]);

	useEffect(() => {
		setValue('currency' as `${string}.${string | number}`, 'USD');
	}, [setValue]);

	useEffect(() => {
		loadCostLog();
	}, [loadCostLog, service]);

	calculateTotalCost();
	return (
		<FormProvider {...methods}>
			<form onSubmit={methods.handleSubmit((data) => handleUpdateService(data as ITransferService))}>
				<Typography variant="h6">Descripción</Typography>
				<Box display="flex" flexDirection="column" gap={2}>
					<Box>
						<Typography color={'Grey'} mb={1}>
							Pasajeros
						</Typography>
						<ControlledAutoComplete
							name={'passengerIds'}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
							}}
							options={passengers}
							isMultiple={true}
							helperText={checkShowErrorMessage(Boolean(errors.passengerIds), errors.passengerIds?.message)}
						/>
					</Box>
					<Grid item xs={12} sm={12}>
						<Typography color={'Grey'}>Referencia</Typography>
						<ControlledTextField
							name={'reference'}
							inputProps={!isExistingService ? { id: SERVICE_FORM_INITIAL_FOCUS_ID } : undefined}
							rules={{
								required: { value: true, message: 'Este campo es requerido' },
							}}
							helperText={checkShowErrorMessage(Boolean(errors.reference), errors.reference?.message)}
						/>
						{tariffProviderIdWatched && tariffProviderIdWatched !== 'n/a' && (
							<ReferenceValidationAlert providerId={tariffProviderIdWatched} reference={referenceValue ?? ''} />
						)}
					</Grid>
					<Grid container spacing={2} alignItems={'center'}>
						<Grid item xs={12}>
							<ProviderPaymentConstraintsAlert constraints={paymentConstraints} />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Proveedor de tarifas</Typography>
							<AutocompleteProviders
								name={'tariffProviderId'}
								disabled={paymentConstraints.lockTariffProvider}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
							/>
						</Grid>
						<Grid item xs={12} sm={4}>
							<ControlledCheckBox
								name={'sameTariffServiceProvider'}
								label="Mismo proveedor de tarifa/servicio"
								onChange={handleCheckboxChange}
							/>
						</Grid>
						{!sameTariffServiceProvider && (
							<Grid item xs={12} sm={4}>
								<Typography color={'Grey'}>Prestador de servicio</Typography>
								<AutocompleteProviders
									name={'serviceProviderId'}
									rules={{
										required: { value: true, message: 'Este campo es requerido' },
									}}
								/>
							</Grid>
						)}
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Unidad por</Typography>
							<ControlledSelect
								name={'unit'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={excludeTypeUnits(typeUnit, ['Habitacion', 'Grupo', 'Camarote'])}
								iconTypeTrip={iconMapping}
							/>
						</Grid>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Moneda</Typography>
							<ControlledSelect
								name={'currency'}
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
								}}
								options={currencyesList.map((item) => ({ id: item.code.toString(), name: item.code }))}
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Typography color={'Grey'}>Costo</Typography>
							<ControlledTextField
								name={'cost'}
								isNumber
								rules={buildMinTotalCostRules(
									paymentConstraints.minAllowedCost,
									paymentConstraints.hasProviderPayments,
									{
										required: { value: true, message: 'Este campo es requerido' },
										min: {
											value: 0,
											message: 'El costo debe ser mayor a -1',
										},
									},
								)}
								onChange={(value) => handleCostChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.cost), errors.cost?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={2}>
							<Typography color={'Grey'}>Markup %</Typography>
							<ControlledTextField
								name={'markupPercentage'}
								isNumber
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
									pattern: {
										value: /^-?\d*\.?\d+$/,
										message: 'El valor debe ser numérico',
									},
								}}
								onChange={(value) => handleMarkupPercentageChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.markupPercentage), errors.markupPercentage?.message)}
							/>
						</Grid>
						<Grid item xs={12} sm={3}>
							<Typography color={'Grey'}>Venta</Typography>
							<ControlledTextField
								name={'saleAmount'}
								isNumber
								rules={{
									required: { value: true, message: 'Este campo es requerido' },
									pattern: {
										value: /^(0|[1-9]\d*)(\.\d+)?$/,
										message: 'El valor debe ser numérico y mayor o igual a 0',
									},
								}}
								onChange={(value) => handleSaleAmountChange(parseFloat(value))}
								helperText={checkShowErrorMessage(Boolean(errors.saleAmount), errors.saleAmount?.message)}
							/>
						</Grid>
						{theCountry === 'CO' && (
							<>
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3} />
								<Grid item xs={12} sm={3}>
									<Typography color={'Grey'}>Venta COP</Typography>
									<ControlledTextField
										name={'saleAmountInLocalCurrency'}
										isNumber
										rules={{
											pattern: {
												value: /^(0|[1-9]\d*)(\.\d+)?$/,
												message: 'El valor debe ser numérico y mayor o igual a 0',
											},
										}}
										onChange={(value) => handleSaleAmountChangeCOP(parseFloat(value))}
										helperText={checkShowErrorMessage(Boolean(errors.saleAmount), errors.saleAmount?.message)}
									/>
								</Grid>
							</>
						)}
					</Grid>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={4} />
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Costo Total</Typography>
							<ControlledTextField name={'totalCost'} disabled />
						</Grid>
						<Grid item xs={12} sm={4}>
							<Typography color={'Grey'}>Venta Total</Typography>
							<ControlledTextField name={'totalSaleAmount'} disabled />
						</Grid>
						{theCountry === 'CO' && (
							<>
								<Grid item xs={2} sm={4} />
								<Grid item xs={2} sm={4}>
									<Typography color={'Grey'}>Tipo de Cambio</Typography>
									<ControlledTextField
										name={'currencyRate'}
										disabled={isExistingService}
										onChange={calculateTotalCost}
									/>
								</Grid>
								<Grid item xs={10} sm={4}>
									<Typography color={'Grey'}>Total en COP</Typography>
									<ControlledTextField name={'totalSaleAmountInLocalCurrency'} disabled />
								</Grid>
							</>
						)}
					</Grid>
					{fields.map((_stretches, stretchesIndex) => (
						<React.Fragment key={`stretches-${stretchesIndex}`}>
							<Box display="flex" justifyContent="space-between" alignItems="end">
								<Box>
									<Typography sx={{ pt: 3, pl: 2, pb: 2 }} variant="h6">
										Tramo {stretchesIndex + 1}
									</Typography>
								</Box>
								<Box>
									<Button
										variant="contained"
										color={isServiceInactive ? 'inherit' : 'error'}
										disabled={isServiceInactive}
										onClick={() => handleDelete(stretchesIndex)}
										sx={
											isServiceInactive
												? {
														backgroundColor: 'action.disabledBackground',
														color: 'action.disabled',
														'&.Mui-disabled': {
															backgroundColor: 'action.disabledBackground',
															color: 'action.disabled',
														},
												  }
												: undefined
										}
									>
										<DeleteIcon />
										Eliminar tramo
									</Button>
								</Box>
							</Box>
							<Grid container spacing={2}>
								<Grid item xs={12} sm={4}>
									<Typography color={'Grey'}>Tipo de traslado</Typography>
									<ControlledSelect
										name={`transferSegments.${stretchesIndex}.transferType`}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
										}}
										options={typeTransfer.map((item) => ({ id: item.id.toString(), name: item.name }))}
										iconTypeTrip={iconType}
									/>
								</Grid>
								<Grid item xs={12} sm={3.7}>
									<Typography color={'Grey'}>Origen</Typography>
									<ControlledTextField
										name={`transferSegments.${stretchesIndex}.originName`}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
										}}
										helperText={checkShowErrorMessage(
											Boolean(errors.transferSegments?.[stretchesIndex]?.originName),
											errors.transferSegments?.[stretchesIndex]?.originName?.message,
										)}
									/>
								</Grid>
								<Grid xs={12} sm={0.6}>
									<ArrowForwardIcon sx={{ color: 'Grey', mt: 5.5, ml: 1.5 }} />
								</Grid>
								<Grid item xs={12} sm={3.7}>
									<Typography color={'Grey'}>Destino</Typography>
									<ControlledTextField
										name={`transferSegments.${stretchesIndex}.destinationName`}
										rules={{
											required: { value: true, message: 'Este campo es requerido' },
										}}
										helperText={checkShowErrorMessage(
											Boolean(errors.transferSegments?.[stretchesIndex]?.destinationName),
											errors.transferSegments?.[stretchesIndex]?.destinationName?.message,
										)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Typography color={'Grey'}>Fecha inicial</Typography>
									<ControlledDatePicker
										name={`transferSegments.${stretchesIndex}.startDate`}
										rules={{ required: 'Este campo es requerido' }}
										required={true}
									/>
								</Grid>
								{(watch(`transferSegments.${stretchesIndex}.transferType`) === TransferTypeEnum.InOut ||
									watch(`transferSegments.${stretchesIndex}.transferType`) === TransferTypeEnum.InOutPrivate) && (
									<Grid item xs={12} sm={6}>
										<Typography color={'Grey'}>Fecha Vuelta / Limite</Typography>
										<ControlledDatePicker
											name={`transferSegments.${stretchesIndex}.endDate`}
											rules={{ required: 'Este campo es requerido' }}
											required={true}
										/>
									</Grid>
								)}
								{(watch(`transferSegments.${stretchesIndex}.transferType`) === TransferTypeEnum.InOut ||
									watch(`transferSegments.${stretchesIndex}.transferType`) === TransferTypeEnum.InOutPrivate) && (
									<Grid item xs={12}>
										<StayDurationHint
											startDateName={`transferSegments.${stretchesIndex}.startDate`}
											endDateName={`transferSegments.${stretchesIndex}.endDate`}
											variant="transfer"
										/>
									</Grid>
								)}
							</Grid>
						</React.Fragment>
					))}
					<Grid item xs={12}>
						<Button
							className={classes.buttonAdd}
							variant="outlined"
							onClick={addstretches}
							disabled={isServiceInactive}
							sx={
								isServiceInactive
									? {
											color: 'action.disabled',
											borderColor: 'action.disabledBackground',
									  }
									: undefined
							}
						>
							+ Agregar tramo
						</Button>
					</Grid>
					<Grid item xs={12} sm={12}>
						<Typography color={'Grey'}>Notas de voucher</Typography>
						<Controller
							// @ts-ignore
							name={'voucherNotes'}
							control={control}
							defaultValue="Favor de presentar voucher en el check-in"
							render={({ field }) => (
								<TextField
									fullWidth
									className={classes.textField}
									variant="outlined"
									multiline
									rows={5}
									{...field}
									helperText={checkShowErrorMessage(Boolean(errors.voucherNotes), errors.voucherNotes?.message)}
								/>
							)}
						/>
					</Grid>
					<Grid item xs={12} sm={12}>
						<Typography color={'Grey'}>
							<LockIcon /> Notas internas
						</Typography>
						<Controller
							// @ts-ignore
							name={'internalNotes'}
							control={control}
							render={({ field }) => (
								<TextField
									fullWidth
									className={classes.textField}
									variant="outlined"
									multiline
									rows={5}
									{...field}
									helperText={checkShowErrorMessage(Boolean(errors.internalNotes), errors.internalNotes?.message)}
								/>
							)}
						/>
					</Grid>
					<ServiceCostVariationLogs filteredArray={filteredArray} />
				</Box>

				<ServiceBillingStatusDetail billingStatusDetail={service.billingStatusDetail} />
				<ServicePurchaseOrderDetail serviceId={service.keyId} serviceType={service.serviceType} />
				<ProviderPaymentDateSection
					hydrateKey={paymentDateHydrateKey}
					control={control}
					watch={watch}
					setValue={setValue}
					getValues={getValues}
					clearErrors={methods.clearErrors}
					resetField={resetField}
					errors={errors}
					servicePath={''}
					providerIdPath={'tariffProviderId'}
					providerAutomaticPaymentDateLine={service.providerAutomaticPaymentDateLine}
				/>
				{(service.enable || service.enable === undefined) && (
					<Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
						<Box>
							{shouldShowVoucherButton(
								tariffProviderId,
								getValues('reference' as `${string}.${string | number}`) || '',
							) && (
								<Button
									variant="outlined"
									color="primary"
									startIcon={<GetAppIcon />}
									onClick={handleVoucherDownload}
									disabled={isVoucherLoading}
								>
									{isVoucherLoading ? 'Procesando...' : 'DESCARGAR VOUCHERS'}
								</Button>
							)}
						</Box>
						<Button type="submit" variant="contained" color="success">
							Guardar
						</Button>
					</Box>
				)}
			</form>

			{/* Modal para descarga de vouchers */}
			<VoucherDownloadModal
				open={isVoucherModalOpen}
				onClose={closeVoucherModal}
				voucherData={voucherData}
				isLoading={isVoucherLoading}
				error={voucherError}
				onForceUpdate={handleForceUpdate}
			/>
		</FormProvider>
	);
};
